import React, { useEffect, useState } from 'react';
import AskIcon from 'shared/components/ui/AskIcon';
import { ChevronRight } from 'lucide-react';
import { openAsk } from 'shared/askDraft';
import { useBreadcrumb, useHideGlobalTopBar } from 'shared/context/BreadcrumbContext';
import { createLineage, validateGraph, listIntegrations, getSchema, saveCanvasLayout, listTemplates } from 'features/evals3/api/evals3Api';
import SimpleEvalForm from 'features/evals3/editor/SimpleEvalForm';
import SamplingField from 'features/evals3/editor/SamplingField';
import { startSimple, readSimple } from 'features/evals3/editor/simpleModel';
import ProposalGraph from 'features/evals3/editor/ProposalGraph';
import { ProviderDropdown, ModelDropdown } from 'features/evals3/editor/modelParts';
import { TARGETS } from 'features/evals3/templates';
import EvalSettingsFields, { DEFAULT_SETTINGS } from 'features/evals3/editor/EvalSettingsFields';
import FilterRulesEditor, { scopesFromSchemas } from 'features/evals3/editor/FilterRulesEditor';
import { useCatalogModels } from 'features/evals3/editor/modelParts';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';

/**
 * Create an eval: name, description, target, filters — the same form the
 * current evals open with — the sample, and the eval-level settings.
 *
 * Two modes. SIMPLE is one check and a rule for which answers pass, built
 * right here: pick the check, set it up, say what passes. TRIAGE opens the
 * editor with the trigger and the Pass / Fail verdicts placed and no steps;
 * the steps are added there (TRO-459). Both are the same kind of eval; the
 * mode is only which editor opens next, remembered on the version.
 *
 * Which agent it scores is a filter (run.agent_name), not a field of its own.
 *
 * The starting graph is validated by the API before it is saved, the same
 * validator the editor shows inline.
 *
 * FROM A TEMPLATE (?template=<id>): the name, description, level, filters
 * and steps come from the template. A one-check template opens as Simple and
 * is edited right here; a template with several steps shows them, and they
 * are edited in the editor after creating. A template's judge steps name no
 * model: one choice here sets it on every judge step. The level is the
 * template's own — its steps read that level's fields.
 */

/** Every judge step of a graph pointed at one model. */
export function withJudgeModel(graph, { integrationId, model }) {
  const g = JSON.parse(JSON.stringify(graph));
  for (const n of Object.values(g.nodes || {})) {
    if (n.instrument === 'llm_judge') n.config = { ...n.config, integration_id: Number(integrationId), model };
  }
  return g;
}
const hasJudge = (graph) => Object.values(graph?.nodes || {}).some((n) => n.instrument === 'llm_judge');

/** The graph a new eval starts with: two verdicts, no first step. */
export function startingGraph() {
  return {
    schema_version: 2,
    entry: null,
    nodes: {
      pass: { type: 'verdict', name: 'Pass', config: { passed: true } },
      fail: { type: 'verdict', name: 'Fail', config: { passed: false, reason_code: 'FAILED' } },
    },
  };
}

function Label({ children }) {
  return <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-secondary)]">{children}</div>;
}

const isEmptyFilter = (f) => !f || ((Array.isArray(f.all) && f.all.length === 0) || (Array.isArray(f.any) && f.any.length === 0));

export default function Eval3CreatePage({ teamId, onCancel, onCreated, templateId = null, onScratch = null }) {
  useBreadcrumb([]);
  useHideGlobalTopBar(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [target, setTarget] = useState('run');
  const [filter, setFilter] = useState({ all: [] });
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState(null);

  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS });
  const [mode, setMode] = useState('simple');                       // simple | triage
  const [simple, setSimple] = useState(() => startSimple('llm_judge', 'run'));
  const [samplePct, setSamplePct] = useState(100);
  const [schemas, setSchemas] = useState({});
  const [integrations, setIntegrations] = useState([]);
  const [scopes, setScopes] = useState(null);
  const modelsFor = useCatalogModels(teamId, integrations);
  const [template, setTemplate] = useState(null);          // the template this page was filled in from
  const [templateGraph, setTemplateGraph] = useState(null); // its steps, when they are more than one check
  const [templateMissing, setTemplateMissing] = useState(false);
  const [judgeModel, setJudgeModel] = useState({ integrationId: '', model: '' });

  useEffect(() => {
    if (!templateId) return undefined;
    let off = false;
    Promise.resolve(listTemplates(teamId)).then((d) => {
      if (off) return;
      const t = (d?.templates || []).find((x) => x.id === templateId);
      if (!t) { setTemplateMissing(true); return; }
      setTemplate(t);
      setName(t.name);
      setDescription(t.description || '');
      setTarget(t.target);
      setFilter(t.applies_when || { all: [] });
      if (readSimple(t.graph)) { setMode('simple'); setSimple(t.graph); setTemplateGraph(null); }
      else { setMode('triage'); setTemplateGraph(t.graph); }
    }).catch(() => { if (!off) setTemplateMissing(true); });
    return () => { off = true; };
  }, [teamId, templateId]);

  // A judge in the template needs a model: start on the team's most recently updated provider.
  // As the Simple form does: the provider touched last, with a key and models to pick from.
  useEffect(() => {
    if (!templateGraph || !hasJudge(templateGraph) || judgeModel.integrationId || !integrations.length) return;
    const withKey = integrations.filter((i) => i.has_api_key !== false);
    const pool = withKey.length ? withKey : integrations;
    const withModels = pool.filter((i) => (modelsFor(i) || []).length || i.default_model);
    const first = [...(withModels.length ? withModels : pool)].sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')))[0];
    setJudgeModel({ integrationId: String(first.id), model: '' });
  }, [templateGraph, integrations, judgeModel.integrationId, modelsFor]);
  const judgeIntegration = integrations.find((i) => String(i.id) === String(judgeModel.integrationId)) || null;
  // The model follows the provider: its default when it offers it, else its first model (the catalog arrives late).
  useEffect(() => {
    if (!judgeIntegration || judgeModel.model) return;
    const models = modelsFor(judgeIntegration) || [];
    const model = judgeIntegration.default_model && (!models.length || models.includes(judgeIntegration.default_model)) ? judgeIntegration.default_model : models[0];
    if (model) setJudgeModel((j) => ({ ...j, model }));
  }, [judgeIntegration, judgeModel.model, modelsFor]);
  const needsJudgeModel = Boolean(templateGraph && hasJudge(templateGraph));

  useEffect(() => { listIntegrations(teamId).then(setIntegrations).catch(() => setIntegrations([])); }, [teamId]);
  useEffect(() => {
    let cancelled = false;
    Promise.all(['run', 'span', 'conversation'].map((t) => getSchema(teamId, t).catch(() => null)))
      .then(([run, span, conversation]) => { if (!cancelled) { setScopes(scopesFromSchemas({ run, span, conversation })); setSchemas({ run, span, conversation }); } });
    return () => { cancelled = true; };
  }, [teamId]);

  // A different target reads different properties; filters written for one do not carry over.
  const pickTarget = (t) => { if (t !== target) { setTarget(t); setFilter({ all: [] }); setSimple(startSimple(kindOfSimple(simple), t)); } };
  const kindOfSimple = (g) => { const n = g?.nodes?.check; return !n ? 'llm_judge' : (n.instrument === 'cross_encoder' || n.instrument === 'bi_encoder') ? 'semantic' : n.instrument; };

  async function submit(e) {
    e.preventDefault();
    if (!name.trim()) { setProblems([{ message: 'Give the eval a name.' }]); return; }
    setBusy(true); setProblems(null);
    if (needsJudgeModel && (!judgeModel.integrationId || !judgeModel.model)) { setProblems([{ message: 'Pick the model the judge steps use.' }]); return; }
    const graph = mode === 'simple' ? simple
      : templateGraph ? (needsJudgeModel ? withJudgeModel(templateGraph, judgeModel) : templateGraph)
        : startingGraph();
    const appliesWhen = isEmptyFilter(filter) ? null : filter;
    try {
      const check = await validateGraph(teamId, { graph, applies_when: appliesWhen });
      if (!check.ok) { setProblems(check.errors); return; }
      const out = await createLineage(teamId, {
        name: name.trim(), description: description.trim() || null, target, sample_pct: samplePct,
        definition: { graph, applies_when: appliesWhen, ...settings }, change_note: template ? `created from the template “${template.name}”` : 'created',
        // A template is a starting point to adapt and test: it starts switched off, and is turned on in Settings.
        ...(template ? { enabled: false } : {}),
      });
      // Which editor opens next lives on the version's ui, beside the canvas layout; never a version of its own.
      if (out?.definition?.version) await Promise.resolve(saveCanvasLayout(teamId, out.lineage.id, out.definition.version, { mode })).catch(() => {});
      onCreated(out.lineage);
    } catch (err) {
      const d = err?.response?.data;
      setProblems(d?.errors || [{ message: d?.message || d?.error || err.message }]);
    } finally { setBusy(false); }
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-[color:var(--surface-base)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex h-8 shrink-0 items-center gap-1.5 border-b border-[color:var(--border-default)] px-4">
        <button type="button" onClick={onCancel} className="text-[13px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">Evals</button>
        <ChevronRight className="h-3 w-3 text-[color:var(--text-muted)]" />
        <span className="text-[13px] font-medium text-[color:var(--text-primary)]">New eval</span>
        <div className="flex-1" />
        {/* The other way in: describe it, and Ask reads the traces, builds it and shows what it would decide. */}
        <button type="button" onClick={() => openAsk('Create an eval that ')}
          title="Describe the eval to Lucid: it reads your traces, builds it, and shows what it would decide before you turn it on"
          className="inline-flex h-6 items-center gap-1 rounded border border-[color:var(--border-default)] px-2 text-[11.5px] font-medium text-[color:var(--text-secondary)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
          <AskIcon size={13} /> Create with Lucid
        </button>
      </div>
      <form onSubmit={submit} className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-5 py-4">
          {templateMissing && (
            <div className="mb-3 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-3 py-2 text-[12px] text-[color:var(--text-secondary)]" role="status">
              There is no template called “{templateId}”. This is a blank eval.
            </div>
          )}
          {template && (
            /* The form is already filled in from the template, which is the
               whole message. Restating the name, when to use it, what to adapt
               and that it starts switched off put a paragraph above a form the
               person came here to fill in. One line, and a way back out. */
            <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="from-template">
              <span className="text-[12px] text-[color:var(--text-muted)]">{template.name}</span>
              <div className="flex-1" />
              {onScratch && <button type="button" onClick={onScratch} className="text-[11.5px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">Start from scratch instead</button>}
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label>Name</Label>
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="e.g. RAG faithfulness" aria-label="Name" className="input w-full" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <input value={description} onChange={(e) => setDescription(e.target.value)} aria-label="Description" placeholder="Briefly describe what this measures." className="input w-full" />
            </div>
          </div>

          <div className="mt-3">
            <Label>Target</Label>
            {template ? (
              <div className="flex items-center gap-2 text-[12px] text-[color:var(--text-secondary)]">
                <span className="rounded-md border border-[color:var(--border-default)] px-2 py-1 font-medium text-[color:var(--text-primary)]">{(TARGETS.find((t) => t.value === target) || {}).label}</span>
                <span className="text-[color:var(--text-muted)]">Set by the template: its steps read {target === 'run' ? 'a run' : target === 'span' ? 'a span' : 'a conversation'}.</span>
              </div>
            ) : (
              <SegmentedTabs ariaLabel="Target" value={target} onChange={pickTarget} options={TARGETS.map((t) => ({ value: t.value, label: t.label }))} />
            )}
          </div>

          {/* Filters sit with Target: they narrow which traces of that target get scored. */}
          <div className="mt-3">
            <Label>Filters</Label>
            <FilterRulesEditor value={filter} onChange={setFilter} target={target} scopes={scopes || { run: [], span: [], conversation: [] }} teamId={teamId} />
          </div>

          <div className="mt-3">
            <Label>Sampling</Label>
            <SamplingField target={target} value={samplePct} onChange={setSamplePct} />
          </div>

          {templateGraph && (
            <div className="mt-5" data-testid="template-steps">
              <Label>Steps</Label>
              <div className="overflow-hidden rounded-lg border border-[color:var(--border-default)]">
                <ProposalGraph base={templateGraph} proposed={templateGraph} target={null} legend={false}
                  height={Math.min(440, 190 + 30 * Object.keys(templateGraph.nodes || {}).length)} />
              </div>
              {needsJudgeModel && (
                <div className="mt-3" data-testid="judge-model">
                  <Label>Judge model</Label>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    <ProviderDropdown integrations={integrations} value={judgeModel.integrationId}
                      onChange={(id) => setJudgeModel({ integrationId: id, model: '' })} />
                    <ModelDropdown integration={judgeIntegration} models={modelsFor(judgeIntegration)} value={judgeModel.model}
                      onChange={(m) => setJudgeModel((j) => ({ ...j, model: m }))} />
                  </div>
                  <p className="mt-1 text-[11.5px] text-[color:var(--text-muted)]">Runs on your own provider key.</p>
                </div>
              )}
            </div>
          )}

          {!template && (
          <div className="mt-5">
            <Label>Mode</Label>
            <SegmentedTabs ariaLabel="Mode" value={mode} onChange={setMode}
              options={[{ value: 'simple', label: 'Simple', hint: 'One check and a rule for what passes' }, { value: 'triage', label: 'Triage', hint: 'Steps that route to each other, built on a canvas' }]} />
            <p className="mt-1 text-[11.5px] text-[color:var(--text-muted)]">
              {mode === 'simple' ? 'One check decides. You can open it in Triage later to add steps.' : 'Build the steps on the canvas after creating. Verdicts are placed for you.'}
            </p>
          </div>
          )}

          {mode === 'simple' && (
            <div className="-mx-5 mt-2 border-y border-[color:var(--border-subtle)] bg-[color:var(--surface-sunken)]/40">
              <SimpleEvalForm graph={simple} onChange={setSimple} target={target} schema={schemas[target] || null} scopes={scopes}
                integrations={integrations} modelsFor={modelsFor} teamId={teamId} />
            </div>
          )}

          <div className="mt-6">
            <EvalSettingsFields target={target} value={settings} onChange={(p) => setSettings((s) => ({ ...s, ...p }))} integrations={integrations} modelsFor={modelsFor} />
          </div>

          {problems && (
            <ul className="mb-4 rounded-md border border-[color:var(--feedback-error)] bg-danger-soft p-2 text-[11.5px] text-danger" aria-live="polite">
              {problems.map((p, i) => <li key={i}>{p.node_id ? <code className="font-mono">{p.node_id}</code> : null} {p.message}</li>)}
            </ul>
          )}
          <div className="flex items-center justify-end gap-2 border-t border-[color:var(--border-subtle)] pt-4">
            <button type="button" onClick={onCancel} className="h-8 rounded-md px-3 text-[12px] text-[color:var(--text-secondary)] hover:bg-surface-hover">Cancel</button>
            <button type="submit" disabled={busy} className="h-8 rounded-md bg-brand px-3.5 text-[12px] font-medium text-ink-onBrand hover:bg-brand-hover disabled:opacity-40">
              {busy ? 'Creating…' : 'Create and open'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
